import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, PencilSimple, Trash, Buildings, ArrowCounterClockwise, Warning } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrg } from '../../contexts/OrgContext';
import {
  adminListOrgs, adminCreateOrg, adminUpdateOrg, adminDeleteOrg,
  adminRestoreOrg, adminHardDeleteOrg, formatAdminError,
} from '../../lib/admin';
import AdminPage from '../../components/admin/AdminPage';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import ArchiveToggle from '../../components/admin/ArchiveToggle';
import { Field, inputCls, selectCls, monoCls, Modal } from '../../components/admin/Field';
import { TimezoneCombobox } from '../../components/admin/TimezoneCombobox';

const ORG_TYPES = ['nursing_home', 'assisted_living', 'hospital', 'home_care'];

export default function AdminOrganizations() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { isSuperAdmin } = useOrg();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | <org>
  const [confirm, setConfirm] = useState(null);
  const [hardConfirm, setHardConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);

  const allowed = user?.role === 'admin' && isSuperAdmin;

  const reload = async () => {
    setLoading(true);
    try {
      const { items } = await adminListOrgs({ archived: viewArchived });
      // When viewing archived: backend returns ALL rows with include_deleted=true,
      // so we filter down to just the soft-deleted ones client-side.
      const filtered = viewArchived
        ? items.filter((o) => o.deleted_at)
        : items;
      setItems(filtered);
    } catch (err) {
      toast.error(formatAdminError('List organizations', err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) reload(); }, [allowed, viewArchived]);

  const onSave = async (form) => {
    setBusy(true);
    try {
      if (editing === 'new') {
        await adminCreateOrg(form);
        toast.success(t('admin_org_created'));
      } else {
        await adminUpdateOrg(editing.id, form);
        toast.success(t('admin_org_updated'));
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError(editing === 'new' ? 'Create organization' : 'Update organization', err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await adminDeleteOrg(confirm.id);
      toast.success(t('admin_org_deleted'));
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Delete organization', err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (org) => {
    setBusy(true);
    try {
      await adminRestoreOrg(org.id);
      toast.success(t('admin_restored'));
      reload();
    } catch (err) {
      toast.error(formatAdminError('Restore organization', err));
    } finally {
      setBusy(false);
    }
  };

  const onHardDelete = async () => {
    setBusy(true);
    try {
      await adminHardDeleteOrg(hardConfirm.id);
      toast.success(t('admin_hard_deleted'));
      setHardConfirm(null);
      reload();
    } catch (err) {
      toast.error(formatAdminError('Permanently delete organization', err));
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) {
    return (
      <AdminPage title={t('admin_orgs_title')}>
        <Toaster position="top-right" richColors />
        <div className="bg-bg-alt rounded-xl p-8 text-center text-ink-soft" data-testid="admin-not-allowed">
          {t('admin_super_only')}
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={t('admin_orgs_title')} description={t('admin_orgs_sub')}>
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-end mb-3 flex-wrap gap-3">
        <div className="text-sm text-ink-soft mr-auto">
          {items.length} {viewArchived ? t('admin_view_archived').toLowerCase() : t('admin_orgs_count')}
        </div>
        {!viewArchived && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            data-testid="admin-org-new"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700"
          >
            <Plus size={16} weight="bold" /> {t('admin_org_new')}
          </button>
        )}
      </div>

      <div className="flex items-center mb-3">
        <ArchiveToggle value={viewArchived} onChange={setViewArchived} />
      </div>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-muted">{t('loading')}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">
            {viewArchived ? t('admin_no_archived') : t('no_results')}
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-orgs-table">
            <thead className="bg-bg-alt text-ink-soft text-left text-xs uppercase tracking-[0.18em]">
              <tr>
                <th className="px-5 py-3 font-medium">{t('admin_field_name')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_type')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_timezone')}</th>
                <th className="px-5 py-3 font-medium">{t('admin_field_status')}</th>
                <th className="px-5 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone_line">
              {items.map((o) => (
                <tr key={o.id} className={`hover:bg-bg-alt/40 ${viewArchived ? 'opacity-70' : ''}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-sage-100 text-sage-600 grid place-items-center">
                        <Buildings size={16} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-medium text-ink">{o.name}</div>
                        {o.address && <div className="text-xs text-ink-muted">{o.address}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink-soft">{t(`org_type_${o.type}`) || o.type}</td>
                  <td className="px-5 py-3 text-ink-soft font-mono text-xs">{o.timezone || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${
                      viewArchived
                        ? 'bg-terracotta-100 text-terracotta-700'
                        : (o.status === 'active' ? 'bg-sage-100 text-sage-700' : 'bg-bg-alt text-ink-muted')
                    }`}>
                      {viewArchived ? t('admin_view_archived').toLowerCase() : (o.status || 'active')}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right whitespace-nowrap">
                    {viewArchived ? (
                      <>
                        <button
                          onClick={() => onRestore(o)}
                          disabled={busy}
                          data-testid={`admin-org-restore-${o.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-sage-100 hover:text-sage-700 mr-1 disabled:opacity-50"
                          aria-label={t('admin_restore')}
                          title={t('admin_restore')}
                        >
                          <ArrowCounterClockwise size={16} />
                        </button>
                        <button
                          onClick={() => setHardConfirm(o)}
                          disabled={busy}
                          data-testid={`admin-org-hard-delete-${o.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700 disabled:opacity-50"
                          aria-label={t('admin_hard_delete')}
                          title={t('admin_hard_delete')}
                        >
                          <Warning size={16} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditing(o)}
                          data-testid={`admin-org-edit-${o.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-bg-alt hover:text-ink mr-1"
                          aria-label={t('edit')}
                        >
                          <PencilSimple size={16} />
                        </button>
                        <button
                          onClick={() => setConfirm(o)}
                          data-testid={`admin-org-delete-${o.id}`}
                          className="p-2 rounded-lg text-ink-soft hover:bg-terracotta-100 hover:text-terracotta-700"
                          aria-label={t('admin_delete')}
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <OrgForm
          org={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSave={onSave}
          busy={busy}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        title={t('admin_org_delete_title')}
        description={confirm ? t('admin_org_delete_confirm', { name: confirm.name }) : ''}
        confirmLabel={t('admin_delete')}
        danger
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={onDelete}
      />

      <ConfirmDialog
        open={!!hardConfirm}
        title={t('admin_hard_delete_title')}
        description={hardConfirm ? t('admin_hard_delete_confirm', { name: hardConfirm.name }) : ''}
        confirmLabel={t('admin_hard_delete')}
        danger
        busy={busy}
        onCancel={() => setHardConfirm(null)}
        onConfirm={onHardDelete}
      />
    </AdminPage>
  );
}

function OrgForm({ org, onCancel, onSave, busy }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    name: org?.name || '',
    type: org?.type || 'nursing_home',
    address: org?.address || '',
    timezone: org?.timezone || 'Europe/Riga',
    status: org?.status || 'active',
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave(form);
  };

  return (
    <Modal onCancel={onCancel}>
      <form
        onSubmit={submit}
        data-testid="admin-org-form"
        className="space-y-4"
      >
        <h2 className="font-heading text-2xl text-ink">
          {org ? t('admin_org_edit_title') : t('admin_org_new_title')}
        </h2>

        <Field label={t('admin_field_name')} required>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="admin-org-name"
            className={inputCls}
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t('admin_field_type')}>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              data-testid="admin-org-type"
              className={selectCls}
            >
              {ORG_TYPES.map((tp) => <option key={tp} value={tp}>{t(`org_type_${tp}`) || tp}</option>)}
            </select>
          </Field>
          <Field label={t('admin_field_timezone')}>
            <TimezoneCombobox
              value={form.timezone}
              onChange={(tz) => setForm({ ...form, timezone: tz })}
              testid="admin-org-timezone"
            />
          </Field>
        </div>

        <Field label={t('admin_field_address')}>
          <input
            type="text"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="admin-org-address"
            className={inputCls}
          />
        </Field>

        <Field label={t('admin_field_status')}>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            data-testid="admin-org-status"
            className={selectCls}
          >
            <option value="active">{t('status_active') || 'active'}</option>
            <option value="inactive">{t('status_inactive') || 'inactive'}</option>
          </select>
        </Field>

        <div className="flex justify-end gap-2 pt-2 border-t border-stone_line">
          <button type="button" onClick={onCancel} disabled={busy}
            data-testid="admin-org-cancel"
            className="px-4 py-2 rounded-lg text-sm text-ink-soft hover:bg-bg-alt disabled:opacity-50">
            {t('cancel')}
          </button>
          <button type="submit" disabled={busy || !form.name.trim()}
            data-testid="admin-org-save"
            className="px-4 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50">
            {busy ? '…' : t('save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Local Field helper removed — now using the shared Field from
// components/admin/Field.js for visual consistency across admin forms.
